import React, { useState, useEffect, useRef } from "react";
import { subscriptionService } from "@/services/subscriptionService";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AnimatedBackground } from "@/components/ui/AnimatedBackground";
import {
  Loader2,
  AlertCircle,
  ArrowLeft,
  Check,
  Building,
  User,
  Mail,
  Phone,
  Globe,
  Lock,
  Eye,
  EyeOff,
  Shield,
  ArrowRight,
  Coins,
} from "lucide-react";
import { countries } from "@/data/countries";
import "flag-icons/css/flag-icons.min.css";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { authAPI } from "@/services/api";

const ENTERPRISE_PLAN_ID = "0602e9cb-d257-11f0-93c8-5254005369ba";

export const PlanRegistrationPage: React.FC = () => {
  const navigate = useNavigate();

  const currencyOptions = [
    { name: "Dólar (USD)", symbol: "$" },
    { name: "Euro (EUR)", symbol: "€" },
    { name: "Quetzal (GTQ)", symbol: "Q" },
    { name: "Libra (GBP)", symbol: "£" },
  ];

  const [registering, setRegistering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [companyCountrySearch, setCompanyCountrySearch] = useState("");
  const [showCompanyCountryDropdown, setShowCompanyCountryDropdown] =
    useState(false);
  const [companyCountryCode, setCompanyCountryCode] = useState("");

  const [adminCountrySearch, setAdminCountrySearch] = useState("");
  const [showAdminCountryDropdown, setShowAdminCountryDropdown] =
    useState(false);
  const [adminCountryCode, setAdminCountryCode] = useState("");

  const companyCountryRef = useRef<HTMLInputElement>(null);
  const adminCountryRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    companyName: "",
    companyEmail: "",
    companyPhone: "",
    website: "",
    currency: "",
    adminFirstName: "",
    adminLastName: "",
    adminEmail: "",
    adminPassword: "",
    confirmPassword: "",
    adminPhone: "",
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const filteredCompanyCountries = countries.filter(
    (c) =>
      c.name.toLowerCase().includes(companyCountrySearch.toLowerCase()) ||
      c.code.includes(companyCountrySearch),
  );

  const filteredAdminCountries = countries.filter(
    (c) =>
      c.name.toLowerCase().includes(adminCountrySearch.toLowerCase()) ||
      c.code.includes(adminCountrySearch),
  );

  const handleSelectCompanyCountry = (country: (typeof countries)[0]) => {
    setCompanyCountryCode(country.code);
    setCompanyCountrySearch(`${country.name} (${country.code})`);
    setShowCompanyCountryDropdown(false);
  };

  const handleSelectAdminCountry = (country: (typeof countries)[0]) => {
    setAdminCountryCode(country.code);
    setAdminCountrySearch(`${country.name} (${country.code})`);
    setShowAdminCountryDropdown(false);
  };

  const validateStep = (step: number): boolean => {
    const errors: Record<string, string> = {};

    if (step === 1) {
      if (!formData.companyName.trim()) {
        errors.companyName = "El nombre de la empresa es requerido";
      } else if (formData.companyName.length < 2) {
        errors.companyName = "El nombre debe tener al menos 2 caracteres";
      }

      if (!formData.companyEmail.trim()) {
        errors.companyEmail = "El email de la empresa es requerido";
      } else if (!/\S+@\S+\.\S+/.test(formData.companyEmail)) {
        errors.companyEmail = "Email no válido";
      }

      if (formData.website && !/^https?:\/\/.+/.test(formData.website)) {
        errors.website = "URL no válida (debe incluir http:// o https://)";
      }
    } else if (step === 2) {
      if (!formData.adminFirstName.trim()) {
        errors.adminFirstName = "El nombre es requerido";
      }

      if (!formData.adminLastName.trim()) {
        errors.adminLastName = "El apellido es requerido";
      }

      if (!formData.adminEmail.trim()) {
        errors.adminEmail = "El email es requerido";
      } else if (!/\S+@\S+\.\S+/.test(formData.adminEmail)) {
        errors.adminEmail = "Email no válido";
      }

      if (!formData.adminPassword) {
        errors.adminPassword = "La contraseña es requerida";
      } else if (formData.adminPassword.length < 8) {
        errors.adminPassword = "La contraseña debe tener al menos 8 caracteres";
      }

      if (formData.adminPassword !== formData.confirmPassword) {
        errors.confirmPassword = "Las contraseñas no coinciden";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleNextStep = () => {
    if (validateStep(step)) {
      if (step < 3) {
        setStep(step + 1);
      } else {
        handleSubmit();
      }
    }
  };

  const handlePreviousStep = () => {
    if (step > 1) {
      setStep(step - 1);
      setFormErrors({});
    }
  };

  const handleSubmit = async () => {
    try {
      setRegistering(true);
      setError(null);

      const finalCompanyPhone =
        companyCountryCode && formData.companyPhone
          ? `${companyCountryCode} ${formData.companyPhone}`
          : formData.companyPhone;

      const finalAdminPhone =
        adminCountryCode && formData.adminPhone
          ? `${adminCountryCode} ${formData.adminPhone}`
          : formData.adminPhone;

      const subscriptionData = {
        companyName: formData.companyName.trim(),
        companyEmail: formData.companyEmail.trim(),
        companyPhone: finalCompanyPhone?.trim() || undefined,
        website: formData.website?.trim() || undefined,
        currency: formData.currency,
        planId: ENTERPRISE_PLAN_ID,
        ownerFirstName: formData.adminFirstName.trim(),
        ownerLastName: formData.adminLastName.trim(),
        ownerEmail: formData.adminEmail.trim(),
        ownerPassword: formData.adminPassword,
        ownerPhone: finalAdminPhone?.trim() || undefined,
        trialDays: 14,
        config: {
          permissions: { "*": true },
        },
      };

      const result =
        await subscriptionService.subscribeCompany(subscriptionData);

      if (result.body.success) {
        setSuccess(true);

        // Optimización: Login en segundo plano mientras se muestra éxito
        const loginPromise = !result.body.data?.auth?.token
          ? authAPI
              .login({
                email: formData.adminEmail,
                password: formData.adminPassword,
              })
              .then((loginRes) => {
                if (loginRes.data?.tokens?.access_token) {
                  localStorage.setItem(
                    "token",
                    loginRes.data.tokens.access_token,
                  );
                  if (loginRes.data.tokens.refresh_token) {
                    localStorage.setItem(
                      "refresh_token",
                      loginRes.data.tokens.refresh_token,
                    );
                  }
                }
              })
              .catch((e) => console.error("Auto-login failed", e))
          : Promise.resolve();

        // Esperar mínimo 1s para UX y que termine el login
        await Promise.all([
          loginPromise,
          new Promise((resolve) => setTimeout(resolve, 1000)),
        ]);

        const hasToken =
          localStorage.getItem("token") || result.body.data?.auth?.token;

        if (hasToken) {
          if (result.body.data?.auth?.token) {
            localStorage.setItem("token", result.body.data.auth.token);
            localStorage.setItem(
              "refresh_token",
              result.body.data.auth.refresh_token,
            );

            if (result.body.data.user) {
              localStorage.setItem(
                "user",
                JSON.stringify(result.body.data.user),
              );
            }
            if (result.body.data.company) {
              localStorage.setItem(
                "company",
                JSON.stringify(result.body.data.company),
              );
            }
          }

          window.location.href = "/dashboard";
        } else {
          navigate("/login", {
            state: {
              message:
                "¡Registro exitoso! Por favor inicia sesión con tus credenciales.",
              email: formData.adminEmail,
            },
          });
        }
      } else {
        throw new Error(
          result.body.message || result.body.error || "Error en el registro",
        );
      }
    } catch (err: any) {
      console.error("❌ Error en registro:", err);
      let errorMessage = "Error en el registro. Por favor intenta nuevamente.";
      if (err.message) errorMessage = err.message;
      setError(errorMessage);
    } finally {
      setRegistering(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen overflow-hidden relative flex items-center justify-center">
        <AnimatedBackground />
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative z-10 w-full max-w-md p-8 bg-white/90 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl text-center"
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
            <Check className="h-10 w-10 text-green-600" />
          </div>
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            ¡Bienvenido a ProGraphic!
          </h2>
          <p className="text-slate-600 mb-8 text-lg">
            Tu cuenta ha sido creada. Iniciando sesión automáticamente...
          </p>
          <div className="flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-hidden relative">
      <AnimatedBackground />

      <div className="relative z-10 flex items-center justify-center min-h-screen px-4 py-4">
        <div className="w-full max-w-md">

          <motion.div
            className="flex flex-col items-center justify-center space-y-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="w-full max-w-md bg-white/80 backdrop-blur-md rounded-2xl shadow-xl border border-white/20 p-4 md:p-5">
              <div className="mb-3">
                <div className="flex items-center justify-between mb-2">
                  <Link
                    to="/"
                    className="text-sm text-slate-500 hover:text-blue-600 flex items-center gap-1 transition-colors"
                  >
                    <ArrowLeft className="h-4 w-4" /> Volver
                  </Link>
                  <div className="flex gap-1">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className={`h-1.5 w-6 rounded-full transition-all duration-300 ${
                          step >= i ? "bg-blue-600" : "bg-slate-200"
                        }`}
                      />
                    ))}
                  </div>
                </div>

                <h1 className="text-xl font-bold text-slate-900">
                  {step === 1
                    ? "Datos de la Empresa"
                    : step === 2
                      ? "Datos del Administrador"
                      : "Confirmar Cuenta"}
                </h1>
                <p className="text-slate-500 text-xs mt-1">
                  {step === 1
                    ? "Configura el perfil de tu organización"
                    : step === 2
                      ? "Crea tu usuario principal"
                      : "Revisa y finaliza el registro"}
                </p>
              </div>

              {error && (
                <Alert
                  variant="destructive"
                  className="mb-4 bg-red-50 border-red-200 text-red-700 py-2"
                >
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {step === 1 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-2.5"
                >
                  <div className="space-y-1">
                    <Label className="text-xs font-medium">
                      Nombre de la Empresa
                    </Label>
                    <div className="relative">
                      <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                      <Input
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleInputChange}
                        className="pl-9 bg-slate-50 border-slate-200 h-9 rounded-lg text-sm"
                        placeholder="Ej: Mi Empresa S.A."
                      />
                    </div>
                    {formErrors.companyName && (
                      <p className="text-xs text-red-500">
                        {formErrors.companyName}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs font-medium">
                      Email Corporativo
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                      <Input
                        name="companyEmail"
                        value={formData.companyEmail}
                        onChange={handleInputChange}
                        className="pl-9 bg-slate-50 border-slate-200 h-9 rounded-lg text-sm"
                        placeholder="contacto@empresa.com"
                      />
                    </div>
                    {formErrors.companyEmail && (
                      <p className="text-xs text-red-500">
                        {formErrors.companyEmail}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs font-medium">Teléfono</Label>
                    <div className="flex gap-2 relative">
                      <div className="relative w-32">
                        <Input
                          ref={companyCountryRef}
                          value={companyCountrySearch}
                          onChange={(e) => {
                            setCompanyCountrySearch(e.target.value);
                            setShowCompanyCountryDropdown(true);
                          }}
                          onFocus={() => setShowCompanyCountryDropdown(true)}
                          placeholder="País"
                          className="bg-slate-50 border-slate-200 h-9 rounded-lg text-sm"
                        />
                        {showCompanyCountryDropdown && (
                          <div className="absolute z-50 bg-white border mt-1 max-h-48 overflow-y-auto w-64 shadow-lg rounded-lg left-0">
                            {filteredCompanyCountries.map((c) => (
                              <div
                                key={c.code}
                                className="cursor-pointer px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-sm"
                                onClick={() => handleSelectCompanyCountry(c)}
                              >
                                <span
                                  className={`fi fi-${c.flagCode} w-4 h-3 rounded-sm`}
                                ></span>
                                <span className="truncate">
                                  {c.name} ({c.code})
                                </span>
                              </div>
                            ))}
                            {filteredCompanyCountries.length === 0 && (
                              <div className="px-3 py-2 text-slate-400 text-sm">
                                No encontrado
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <Input
                        name="companyPhone"
                        value={formData.companyPhone}
                        onChange={handleInputChange}
                        placeholder="Número"
                        className="flex-1 bg-slate-50 border-slate-200 h-9 rounded-lg text-sm"
                      />
                    </div>
                    {formErrors.companyPhone && (
                      <p className="text-xs text-red-500">
                        {formErrors.companyPhone}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs font-medium">
                      Sitio Web (Opcional)
                    </Label>
                    <div className="relative">
                      <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                      <Input
                        name="website"
                        value={formData.website}
                        onChange={handleInputChange}
                        className="pl-9 bg-slate-50 border-slate-200 h-9 rounded-lg text-sm"
                        placeholder="https://empresa.com"
                      />
                    </div>
                    {formErrors.website && (
                      <p className="text-xs text-red-500">
                        {formErrors.website}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs font-medium">Moneda</Label>
                    <div className="relative">
                      <Coins className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 z-10" />
                      <Select
                        value={formData.currency}
                        onValueChange={(value) =>
                          setFormData((prev) => ({ ...prev, currency: value }))
                        }
                      >
                        <SelectTrigger className="pl-9 bg-slate-50 border-slate-200 h-9 rounded-lg text-sm">
                          <SelectValue placeholder="Selecciona moneda" />
                        </SelectTrigger>
                        <SelectContent>
                          {currencyOptions.map((option) => (
                            <SelectItem key={option.name} value={option.symbol}>
                              {option.name} - {option.symbol}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-2.5"
                >
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs font-medium">Nombre</Label>
                      <Input
                        name="adminFirstName"
                        value={formData.adminFirstName}
                        onChange={handleInputChange}
                        className="bg-slate-50 border-slate-200 h-9 rounded-lg text-sm"
                        placeholder="Juan"
                      />
                      {formErrors.adminFirstName && (
                        <p className="text-xs text-red-500">
                          {formErrors.adminFirstName}
                        </p>
                      )}
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-medium">Apellido</Label>
                      <Input
                        name="adminLastName"
                        value={formData.adminLastName}
                        onChange={handleInputChange}
                        className="bg-slate-50 border-slate-200 h-9 rounded-lg text-sm"
                        placeholder="Pérez"
                      />
                      {formErrors.adminLastName && (
                        <p className="text-xs text-red-500">
                          {formErrors.adminLastName}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs font-medium">
                      Email Administrador
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                      <Input
                        name="adminEmail"
                        value={formData.adminEmail}
                        onChange={handleInputChange}
                        className="pl-9 bg-slate-50 border-slate-200 h-9 rounded-lg text-sm"
                        placeholder="admin@empresa.com"
                      />
                    </div>
                    {formErrors.adminEmail && (
                      <p className="text-xs text-red-500">
                        {formErrors.adminEmail}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs font-medium">Contraseña</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                      <Input
                        type={showPassword ? "text" : "password"}
                        name="adminPassword"
                        value={formData.adminPassword}
                        onChange={handleInputChange}
                        className="pl-9 pr-9 bg-slate-50 border-slate-200 h-9 rounded-lg text-sm"
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? (
                          <EyeOff className="h-3.5 w-3.5" />
                        ) : (
                          <Eye className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </div>
                    {formErrors.adminPassword && (
                      <p className="text-xs text-red-500">
                        {formErrors.adminPassword}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs font-medium">
                      Confirmar Contraseña
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                      <Input
                        type={showConfirmPassword ? "text" : "password"}
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        className="pl-9 pr-9 bg-slate-50 border-slate-200 h-9 rounded-lg text-sm"
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-3.5 w-3.5" />
                        ) : (
                          <Eye className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </div>
                    {formErrors.confirmPassword && (
                      <p className="text-xs text-red-500">
                        {formErrors.confirmPassword}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs font-medium">
                      Teléfono Admin (Opcional)
                    </Label>
                    <div className="flex gap-2 relative">
                      <div className="relative w-32">
                        <Input
                          ref={adminCountryRef}
                          value={adminCountrySearch}
                          onChange={(e) => {
                            setAdminCountrySearch(e.target.value);
                            setShowAdminCountryDropdown(true);
                          }}
                          onFocus={() => setShowAdminCountryDropdown(true)}
                          placeholder="País"
                          className="bg-slate-50 border-slate-200 h-9 rounded-lg text-sm"
                        />
                        {showAdminCountryDropdown && (
                          <div className="absolute z-50 bg-white border mt-1 max-h-48 overflow-y-auto w-64 shadow-lg rounded-lg left-0">
                            {filteredAdminCountries.map((c) => (
                              <div
                                key={c.code}
                                className="cursor-pointer px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-sm"
                                onClick={() => handleSelectAdminCountry(c)}
                              >
                                <span
                                  className={`fi fi-${c.flagCode} w-4 h-3 rounded-sm`}
                                ></span>
                                <span className="truncate">
                                  {c.name} ({c.code})
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <Input
                        name="adminPhone"
                        value={formData.adminPhone}
                        onChange={handleInputChange}
                        placeholder="Número"
                        className="flex-1 bg-slate-50 border-slate-200 h-9 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                </motion.div>
              )}

              {step === 3 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-3"
                >
                  <div className="bg-slate-50/80 rounded-lg p-3 border border-slate-200 space-y-2">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                        <Building className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Empresa</p>
                        <p className="font-medium text-sm text-slate-900">
                          {formData.companyName}
                        </p>
                        <p className="text-xs text-slate-500">
                          {formData.companyEmail}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 pt-2 border-t border-slate-200">
                      <div className="p-2 bg-purple-100 rounded-lg text-purple-600">
                        <User className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Administrador</p>
                        <p className="font-medium text-sm text-slate-900">
                          {formData.adminFirstName} {formData.adminLastName}
                        </p>
                        <p className="text-xs text-slate-500">
                          {formData.adminEmail}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="text-xs text-center text-slate-500">
                    Al hacer clic en "Completar Registro", aceptas nuestros{" "}
                    <a href="#" className="text-blue-600 hover:underline">
                      Términos de Servicio
                    </a>{" "}
                    y{" "}
                    <a href="#" className="text-blue-600 hover:underline">
                      Política de Privacidad
                    </a>
                    .
                  </div>
                </motion.div>
              )}

              <div className="mt-4 flex gap-3">
                {step > 1 && (
                  <Button
                    variant="outline"
                    onClick={handlePreviousStep}
                    className="flex-1 h-9 rounded-full border-slate-200 text-sm"
                    disabled={registering}
                  >
                    Atrás
                  </Button>
                )}
                <Button
                  onClick={handleNextStep}
                  className={`flex-1 h-9 rounded-full bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 text-white shadow-lg transition-all hover:shadow-xl text-sm ${
                    step === 1 ? "w-full" : ""
                  }`}
                  disabled={registering}
                >
                  {registering ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />
                      Registrando...
                    </>
                  ) : step === 3 ? (
                    <>
                      Completar Registro <Check className="h-3.5 w-3.5 ml-2" />
                    </>
                  ) : (
                    <>
                      Continuar <ArrowRight className="h-3.5 w-3.5 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </div>

            <div className="text-center">
              <p className="text-xs flex items-center justify-center text-slate-500">
                <Shield className="h-3 w-3 mr-1 text-blue-500" />
                Tus datos están protegidos y encriptados
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PlanRegistrationPage;
